import styles from './../MaskOverlay.module.scss';

import { type JSX, PropsWithChildren } from 'react';
import { FormProvider } from 'react-hook-form';

import { TempFileManagerContext } from '@work4all/data/lib/hooks/data-provider/useTempFileManager';

import { useEventEmitter } from '@work4all/utils/lib/EventEmitter';

import { MaskTabContext } from '../../mask-tabs';
import {
  Form,
  MaskOverlayHeader,
  MaskOverlayStandardSubmitButton,
} from '../components';
import { MaskOverlayDeleteButton } from '../components/MaskOverlayDeleteButton';
import { MaskOverlayFullscreenToggleButton } from '../components/MaskOverlayFullscreenToggleButton';
import { MaskOverlayMenuWrapper } from '../components/MaskOverlayMenuWrapper';
import { MaskContextProvider } from '../hooks/mask-context';
import { EntityRightsContext } from '../hooks/use-entity-rights';
import { useOverlayControllerFavoriteItemEntityEvents } from '../hooks/use-overlay-controller-favorite-item-entity-events';
import { useOverlayControllerMoreActions } from '../hooks/use-overlay-controller-more-actions';
import { useStandardDeleteEntityHandler } from '../hooks/use-standard-delete-entity-handler';
import { MaskControllerProps } from '../types';

import { useMaskOverlay } from './use-mask-overlay';

interface OverlayControllerProps<T>
  extends ReturnType<typeof useMaskOverlay<T>>,
    MaskControllerProps {
  onSubmit: (values: T) => Promise<void | T>;
  tabs?: JSX.Element;
  actions?: JSX.Element;
  moreActions?: JSX.Element;
  disableDelete?: boolean;
  showFullscreen?: boolean;
  submitButton?: JSX.Element;
  hideDeleteButton?: boolean;
}

export function OverlayController<T>(
  props: PropsWithChildren<OverlayControllerProps<T>>
) {
  const {
    mask,
    onSubmit,
    tabs,
    children,
    actions,
    form,
    maskContext,
    title,
    subTitle,
    submitButtonProps,
    entityRights,
    tempFileManager,
    moreActions: defaultMoreActions,
    disableDelete,
    isDirty,
    showFullscreen = true,
    submitButton,
    hideDeleteButton = false,
  } = props;

  // delete props
  const handleDeleteEntitiesClick = useStandardDeleteEntityHandler(props);
  const moreActions = useOverlayControllerMoreActions<T>({
    data: form.watch() as T,
    entity: mask.entity,
    defaultActions: defaultMoreActions,
  });

  useOverlayControllerFavoriteItemEntityEvents(form);
  const { triggerEvent } = useEventEmitter();

  return (
    <MaskContextProvider value={maskContext}>
      <EntityRightsContext.Provider value={entityRights}>
        <FormProvider {...form}>
          <MaskTabContext defaultValue={mask.params?.tab ?? 'general'}>
            <Form
              className={styles.maskForm}
              onSubmit={form.handleSubmit(async (data) => {
                await triggerEvent('beforeSubmit', null);
                await onSubmit(data);
              })}
            >
              <TempFileManagerContext.Provider value={tempFileManager}>
                <MaskOverlayHeader
                  title={title}
                  subTitle={subTitle}
                  actions={
                    <>
                      {actions}
                      {submitButton || (
                        <MaskOverlayStandardSubmitButton
                          {...submitButtonProps}
                          isDirty={isDirty}
                        />
                      )}
                      {showFullscreen && <MaskOverlayFullscreenToggleButton />}
                      {mask.isEditMode && (
                        <>
                          {!hideDeleteButton && (
                            <MaskOverlayDeleteButton
                              disabled={
                                !entityRights.delete ||
                                mask.wip ||
                                disableDelete
                              }
                              onClick={handleDeleteEntitiesClick}
                            />
                          )}
                          {moreActions && (
                            <MaskOverlayMenuWrapper>
                              {moreActions}
                            </MaskOverlayMenuWrapper>
                          )}
                        </>
                      )}
                    </>
                  }
                  tabs={tabs}
                />
                {children}
              </TempFileManagerContext.Provider>
            </Form>
          </MaskTabContext>
        </FormProvider>
      </EntityRightsContext.Provider>
    </MaskContextProvider>
  );
}
